import { useContext } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { TenantContext } from "@nens/ranalib";

import {
  JOB_SEARCH_KEY,
  PROCESS_SEARCH_KEY,
  SELECTED_DATASET_SEARCH_KEY,
} from "@/config/query-parameter-consts";
import { getProjectTabQueryParam } from "@/features/project";
import { useTenantActionAllowed } from "@/hooks/use-action-allowed-in-tenant";
import { DatasetOverview } from "@/lib/catalogue/features/dataset";
import { SchematisationsOverview } from "@/lib/catalogue/features/schematisations/schematisations-overview";
import { useProcessByTagQueryDirectly } from "@/queries/query-process";

import { CatalogueSection } from "./components/catalogue-section";

const CatalogueDatasetOverview = ({ isSectionExpanded }: { isSectionExpanded?: boolean }) => {
  const { tenant } = useContext(TenantContext);
  const navigate = useNavigate();
  const processByTagQueryDirectly = useProcessByTagQueryDirectly();

  return (
    <DatasetOverview
      isSectionExpanded={isSectionExpanded}
      goToExtractDatasetProcess={async (params) => {
        const baseUrl = `/${tenant}/projects/${params.projectSlug}/?${getProjectTabQueryParam(
          "processes"
        )}`;
        if ("jobId" in params) return navigate(`${baseUrl}&${JOB_SEARCH_KEY}=${params.jobId}`);
        const { isSuccess, data: process } = await processByTagQueryDirectly("extract_dataset");
        if (isSuccess)
          return navigate(
            `${baseUrl}&${PROCESS_SEARCH_KEY}=${process.id}&${SELECTED_DATASET_SEARCH_KEY}=${params.datasetId}`
          );
      }}
    />
  );
};

export const Catalogue = () => {
  const intl = useIntl();

  const { permissionsLoading, canReadDatasetList } = useTenantActionAllowed();

  if (permissionsLoading) {
    return null;
  }

  // "canReadDatasetList" is the permission that represents the ability to read tenant specific data in the catalogue
  // Datasets are not tenant specific, but model schematisations are.
  // Therefore only show the datasets overview if the user does not have the permission to read tenant specific data in the catalogue.
  if (!canReadDatasetList) {
    return <CatalogueDatasetOverview />;
  }

  return (
    <>
      <CatalogueSection
        id="dataset-overview"
        openText={intl.formatMessage({
          id: "button.open_all_datasets",
          defaultMessage: "All datasets",
        })}
      >
        {(isExpanded) => <CatalogueDatasetOverview isSectionExpanded={isExpanded} />}
      </CatalogueSection>

      <CatalogueSection
        id="schematisation-overview"
        openText={intl.formatMessage({
          id: "button.open_all_schematisations",
          defaultMessage: "All model schematisations",
        })}
      >
        {(isExpanded) => <SchematisationsOverview isSectionExpanded={isExpanded} />}
      </CatalogueSection>
    </>
  );
};
